/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {DesktopHandoffInfoResponse} from '@app/actions/AuthenticationActionCreators';
import type {DesktopHandoffMode} from '@app/components/auth/auth_login_core/useDesktopHandoffFlow';
import styles from '@app/components/auth/HandoffApprovalFlow.module.css';
import {Input} from '@app/components/form/Input';
import {Button} from '@app/components/uikit/button/Button';
import i18n from '@app/I18n';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckCircleIcon, ShieldWarningIcon} from '@phosphor-icons/react';
import type React from 'react';
import {useCallback, useState} from 'react';

const CODE_LENGTH = 12;
const VALID_CODE_PATTERN = /^[A-Za-z0-9]{12}$/;

function formatCodeForDisplay(raw: string): string {
	const cleaned = raw
		.replace(/[^A-Za-z0-9]/g, '')
		.toUpperCase()
		.slice(0, CODE_LENGTH);

	if (cleaned.length <= 6) {
		return cleaned;
	}
	return `${cleaned.slice(0, 6)}-${cleaned.slice(6)}`;
}

function extractRawCode(formatted: string): string {
	return formatted
		.replace(/[^A-Za-z0-9]/g, '')
		.toUpperCase()
		.slice(0, CODE_LENGTH);
}

function formatLocation(location: {city?: string | null; region?: string | null; country?: string | null}): string {
	const parts = [location.city, location.region, location.country].filter(Boolean);
	return parts.length > 0 ? parts.join(', ') : i18n._(msg`Unknown`);
}

interface HandoffApprovalFlowProps {
	mode: DesktopHandoffMode;
	error: string | null;
	clientInfo: DesktopHandoffInfoResponse['client_info'];
	onProceedToCodeInput: () => void;
	onSubmitCode: (code: string) => void;
	onApprove: () => void;
	onDeny: () => void;
	onRetry: () => void;
}

export function HandoffApprovalFlow({
	mode,
	error,
	clientInfo,
	onProceedToCodeInput,
	onSubmitCode,
	onApprove,
	onDeny,
	onRetry,
}: HandoffApprovalFlowProps) {
	const {t} = useLingui();

	const [codeInput, setCodeInput] = useState('');

	const handleCodeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const rawCode = extractRawCode(e.target.value);
			setCodeInput(rawCode);

			if (VALID_CODE_PATTERN.test(rawCode)) {
				onSubmitCode(rawCode);
			}
		},
		[onSubmitCode],
	);

	if (mode === 'warning') {
		return (
			<div className={styles.container}>
				<div className={styles.warningBox}>
					<ShieldWarningIcon size={32} weight="fill" className={styles.warningIcon} />
					<h1 className={styles.warningTitle}>
						<Trans>Desktop sign-in request</Trans>
					</h1>
					<p className={styles.warningDescription}>
						<Trans>
							You are about to authorise a desktop app to sign in to your account. Only continue if you initiated this
							request from a device you control.
						</Trans>
					</p>
				</div>
				<Button onClick={onProceedToCodeInput} variant="primary" fitContainer>
					<Trans>Continue</Trans>
				</Button>
			</div>
		);
	}

	if (mode === 'code_input') {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Enter the code</Trans>
				</h1>
				<p className={styles.description}>
					<Trans>Enter the code shown on the desktop app to continue.</Trans>
				</p>
				<div className={styles.codeInputSection}>
					<Input
						aria-label={t`Login code`}
						value={formatCodeForDisplay(codeInput)}
						onChange={handleCodeChange}
						autoComplete="off"
						autoFocus
					/>
				</div>
			</div>
		);
	}

	if (mode === 'fetching_info') {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Verifying code...</Trans>
				</h1>
				<div className={styles.spinner}>
					<span className={styles.spinnerIcon} />
				</div>
			</div>
		);
	}

	if (mode === 'approving') {
		const platform = clientInfo?.platform ?? t`Unknown`;
		const os = clientInfo?.os ?? t`Unknown`;
		const location = clientInfo?.location ? formatLocation(clientInfo.location) : t`Unknown`;

		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Was this you?</Trans>
				</h1>
				<p className={styles.description}>
					<Trans>A sign-in request was made from the following device. Only approve if you recognise it.</Trans>
				</p>
				<div className={styles.deviceCard}>
					<div className={styles.deviceRow}>
						<span className={styles.deviceLabel}>
							<Trans>Platform</Trans>
						</span>
						<span className={styles.deviceValue}>{platform}</span>
					</div>
					<div className={styles.deviceRow}>
						<span className={styles.deviceLabel}>
							<Trans>Operating system</Trans>
						</span>
						<span className={styles.deviceValue}>{os}</span>
					</div>
					<div className={styles.deviceRow}>
						<span className={styles.deviceLabel}>
							<Trans>Location</Trans>
						</span>
						<span className={styles.deviceValue}>{location}</span>
					</div>
				</div>
				<div className={styles.buttonRow}>
					<Button onClick={onDeny} variant="secondary">
						<Trans>Deny</Trans>
					</Button>
					<Button onClick={onApprove} variant="primary">
						<Trans>Approve</Trans>
					</Button>
				</div>
			</div>
		);
	}

	if (mode === 'completing') {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Completing sign-in...</Trans>
				</h1>
				<div className={styles.spinner}>
					<span className={styles.spinnerIcon} />
				</div>
			</div>
		);
	}

	if (mode === 'done') {
		return (
			<div className={styles.container}>
				<CheckCircleIcon size={48} weight="fill" className={styles.successIcon} />
				<h1 className={styles.title}>
					<Trans>Sign-in approved</Trans>
				</h1>
				<p className={styles.description}>
					<Trans>The desktop app should now be signed in to your account.</Trans>
				</p>
			</div>
		);
	}

	if (mode === 'error') {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Something went wrong</Trans>
				</h1>
				{error ? <p className={styles.error}>{error}</p> : null}
				<Button onClick={onRetry} fitContainer>
					<Trans>Try again</Trans>
				</Button>
			</div>
		);
	}

	return null;
}

interface ConnectedHandoffApprovalFlowProps {
	handoff: {
		mode: DesktopHandoffMode;
		error: string | null;
		clientInfo: DesktopHandoffInfoResponse['client_info'];
		proceedToCodeInput: () => void;
		submitCode: (code: string) => void;
		approve: () => void;
		deny: () => void;
		retry: () => void;
	};
}

export function ConnectedHandoffApprovalFlow({handoff}: ConnectedHandoffApprovalFlowProps) {
	return (
		<HandoffApprovalFlow
			mode={handoff.mode}
			error={handoff.error}
			clientInfo={handoff.clientInfo}
			onProceedToCodeInput={handoff.proceedToCodeInput}
			onSubmitCode={handoff.submitCode}
			onApprove={handoff.approve}
			onDeny={handoff.deny}
			onRetry={handoff.retry}
		/>
	);
}
